<script setup>
import { ref, useTemplateRef, onMounted, watch, nextTick, computed } from 'vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ReadMore from './ReadMore.vue';

const props = defineProps({
  author: {
    type: String,
    default: '',
  },
  message: {
    type: Object,
    default: () => ({}),
  },
  searchTerm: {
    type: String,
    default: '',
  },
});

const messageContent = computed(() => {
  // We perform search on either content or email subject or transcribed text
  if (props.message.content) {
    return props.message.content;
  }

  const { content_attributes = {} } = props.message;
  const { email = {} } = content_attributes || {};
  if (email.subject) {
    return email.subject;
  }

  const audioAttachment = (props.message.attachments || []).find(
    attachment => attachment.file_type === 'audio'
  );
  return audioAttachment?.transcribed_text || '';
});

const { highlightContent } = useMessageFormatter();

const messageContainer = useTemplateRef('messageContainer');
const isOverflowing = ref(false);

const setOverflow = () => {
  const wrap = messageContainer.value;
  if (wrap) {
    const message = wrap.querySelector('.message-content');
    isOverflowing.value = message.offsetHeight > 150;
  }
};

const escapeHtml = html => {
  var text = document.createTextNode(html);
  var p = document.createElement('p');
  p.appendChild(text);
  return p.innerText;
};

const prepareContent = () => {
  const content = messageContent.value || '';
  const escapedText = escapeHtml(content);
  return highlightContent(
    escapedText,
    props.searchTerm,
    'searchkey--highlight'
  );
};

onMounted(() => {
  watch(() => {
    return messageContainer.value;
  }, setOverflow);

  nextTick(setOverflow);
});
</script>

<template>
  <blockquote ref="messageContainer" class="message">
    <p class="header">
      <strong>{{ author }}</strong>
      {{ $t('SEARCH.WROTE') }}
    </p>
    <ReadMore :shrink="isOverflowing" @expand="isOverflowing = false">
      <div v-dompurify-html="prepareContent()" class="message-content" />
    </ReadMore>
  </blockquote>
</template>

<style scoped lang="scss">
.message {
  margin-top: 8px;
  padding: 0 0 0 10px;
  border-left: 2px solid var(--wlab-border-light, var(--n-weak));
}

.message-content::v-deep p,
.message-content::v-deep li::marker {
  margin-bottom: 4px;
  color: var(--wlab-text-muted, var(--n-slate-11));
}

.header {
  margin-bottom: 4px;
  color: var(--wlab-text-muted, var(--n-slate-11));
  font-size: 12px;
}

.message-content {
  color: var(--wlab-text-secondary, var(--n-slate-11));
  word-break: break-word;
}

.message-content::v-deep .searchkey--highlight {
  color: var(--wlab-text-primary, var(--n-slate-12));
  font-size: 13px;
  font-weight: 600;
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 18%, transparent);
  border-radius: 3px;
  padding: 0 2px;
}
</style>
